/**
 * Per-item verification card + trusted-provider links for the sitter dashboard.
 * Sitters upload a document per item, then wait for admin approval — they
 * cannot self-approve.
 */
import React, { useState } from "react";
import { Check, Clock, Upload as UploadIcon, ExternalLink, ShieldCheck } from "lucide-react";
import { api, extractError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import FileUpload from "@/components/FileUpload";

export const VERIFICATION_ITEMS = [
  {
    key: "identity",
    approval_key: "identity_verified",
    label: "Identity verification",
    what: "A clear photo or scan of your government-issued ID (driver's license, state ID, or passport). Make sure your name and photo are readable.",
    providers: [],
  },
  {
    key: "background_check",
    approval_key: "background_check",
    label: "Criminal background check",
    what: "A recent (within 12 months) criminal background check report. Upload the full PDF result — we accept Checkr, Sterling, and most major providers.",
    providers: [
      { name: "Checkr", url: "https://checkr.com/pricing", detail: "~$25, results in 24-48h" },
      { name: "Sterling", url: "https://www.sterlingcheck.com/individual/", detail: "Enterprise-grade" },
      { name: "BeenVerified", url: "https://www.beenverified.com/", detail: "DIY, cheapest" },
    ],
  },
  {
    key: "child_abuse_registry",
    approval_key: "child_abuse_registry",
    label: "Child-abuse registry check",
    what: "A clearance letter from your state's child-abuse & neglect registry.",
    providers: [
      { name: "Georgia (GBI)", url: "https://gbi.georgia.gov/services/gbi-crime-information-center-cic", detail: "GA sitters" },
      { name: "Texas (DFPS)", url: "https://www.dfps.texas.gov/child_protection/background_checks/", detail: "TX sitters" },
      { name: "Other states", url: "https://www.childwelfare.gov/topics/systemwide/laws-policies/state/", detail: "Find yours" },
    ],
  },
  {
    key: "cpr_certification",
    approval_key: "cpr_certification",
    label: "CPR certification",
    what: "A photo of your CPR card. Both sides if the expiration is on the back. American Red Cross or American Heart Association preferred.",
    providers: [
      { name: "American Red Cross", url: "https://www.redcross.org/take-a-class/cpr", detail: "~$40, in-person + online" },
      { name: "American Heart Association", url: "https://cpr.heart.org/en/cpr-courses-and-kits", detail: "Gold standard" },
    ],
  },
  {
    key: "references",
    approval_key: "references_verified",
    label: "References",
    what: "A short document (Word, PDF, or Google Docs export) listing 2–3 references with name, relationship, phone, and email. We'll contact them.",
    providers: [],
  },
  {
    key: "license",
    approval_key: "license_verified",
    label: "Professional license (optional)",
    what: "If you're an RBT, BCBA, teacher, nurse, etc. — upload proof of your active license or credential. Skip if not applicable.",
    providers: [],
    optional: true,
  },
  {
    key: "insurance",
    approval_key: "insurance_verified",
    label: "Liability insurance (optional)",
    what: "If you carry personal liability insurance for babysitting work, upload the certificate. Skip if not applicable.",
    providers: [],
    optional: true,
  },
];

function StatusBadge({ uploaded, approved }) {
  if (approved) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-sage-100 text-sage-800">
        <Check className="w-3 h-3" /> Approved
      </span>
    );
  }
  if (uploaded) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-ochre-100 text-ochre-800">
        <Clock className="w-3 h-3" /> Pending review
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-stone2-100 text-stone2-600">
      Not uploaded
    </span>
  );
}

function VerificationItemCard({ item, uploaded, approved, onUploaded }) {
  const handleUpload = async (upload) => {
    try {
      await api.put("/sitters/me/documents", {
        item_key: item.key,
        upload_path: upload.path,
      });
      toast.success(`${item.label} uploaded — pending admin review`);
      onUploaded();
    } catch (err) { toast.error(extractError(err)); }
  };

  const uploadedPath = uploaded?.path || uploaded;  // legacy vs new shape
  const uploadedUrl = uploadedPath ? `${BACKEND_URL}/api/uploads/${uploadedPath}` : null;

  return (
    <li className="rounded-xl border border-stone2-200 bg-white p-5" data-testid={`sitter-verif-item-${item.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-semibold text-sage-900">
              {item.label}
              {item.optional && <span className="ml-1 text-[10px] uppercase tracking-wider text-stone2-400">optional</span>}
            </span>
            <StatusBadge uploaded={!!uploadedPath} approved={approved} />
          </div>
          <p className="mt-1.5 text-xs text-stone2-600 leading-relaxed">
            <strong className="text-sage-900">What we need:</strong> {item.what}
          </p>
          {item.providers.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-bold text-stone2-500">Get it here:</span>
              {item.providers.map((p) => (
                <a key={p.name} href={p.url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-[11px] font-semibold text-sage-700 hover:text-sage-900 underline underline-offset-2">
                  {p.name} <ExternalLink className="w-3 h-3" />
                  <span className="text-stone2-400 font-normal">({p.detail})</span>
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <FileUpload
            testId={`sitter-upload-${item.key}`}
            label={uploadedPath ? "Replace" : "Upload"}
            onUploaded={handleUpload}
          />
          {uploadedUrl && (
            <a href={uploadedUrl} target="_blank" rel="noreferrer"
               data-testid={`sitter-view-doc-${item.key}`}
               className="text-[11px] text-sage-700 hover:text-sage-900 font-semibold inline-flex items-center gap-1">
              <UploadIcon className="w-3 h-3" /> View upload
            </a>
          )}
        </div>
      </div>
    </li>
  );
}

export default function SitterVerificationSection({ profile, onChanged }) {
  const verifs = profile.verifications || {};
  const uploads = verifs.uploads || {};

  // Support legacy single-upload path for CPR (certification_upload_path)
  if (verifs.certification_upload_path && !uploads.cpr_certification) {
    uploads.cpr_certification = { path: verifs.certification_upload_path };
  }

  return (
    <section className="mt-8 paper-card p-6" data-testid="sitter-verification-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="section-eyebrow">Trust & safety</span>
          <h2 className="mt-2 font-heading font-semibold text-sage-900 text-2xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-sage-600" /> Get verified
          </h2>
          <p className="text-sm text-stone2-600 mt-1 max-w-xl leading-relaxed">
            Upload one document per item below. Our admin team reviews each one — usually within 1–2 business days —
            and awards your <strong>Verified</strong> badge once the four core items are approved.
          </p>
        </div>
        <div className="rounded-lg bg-sage-50 border border-sage-200 px-4 py-3 text-xs text-sage-900 max-w-xs">
          <strong className="block mb-1">Why verified matters:</strong>
          Families are 5× more likely to hire verified sitters, and only verified sitters appear in the top search results.
        </div>
      </div>

      <ul className="mt-6 space-y-3">
        {VERIFICATION_ITEMS.map((item) => (
          <VerificationItemCard
            key={item.key}
            item={item}
            uploaded={uploads[item.key]}
            approved={!!verifs[item.approval_key]}
            onUploaded={onChanged}
          />
        ))}
      </ul>
    </section>
  );
}
